import * as XLSX from "xlsx";
import {
  expandSentinels, filterSelected, isSelectAll, resolveMulti,
} from "Components/SbJdCreationDrawerV2/helpers";

export const BATCH_LIMIT = 50;

export const TEMPLATE_SHEET = 'JD Upload Template';

// Hidden template sheet whose row 1 holds each column's field key
export const FIELD_KEYS_SHEET = 'FIELD_KEYS';

// Hidden sheets the template's VLOOKUP formulas read from
export const JOB_LOOKUP_SHEET = 'LOOKUP';
export const REPORTING_TO_LOOKUP_SHEET = 'REPORTING_TO_LOOKUP';

// Returned (as { error, columns }) when template headers are missing from the file
export const MISSING_COLUMNS = 'MISSING_COLUMNS';
// Returned (as { error, columns }) when a template header appears more than once
export const DUPLICATE_COLUMNS = 'DUPLICATE_COLUMNS';
// Returned when the org has no Specialty but the file's Specialty column has values
export const SPECIALTY_NOT_USED = 'SPECIALTY_NOT_USED';

// Template keys filled by a Job Code VLOOKUP → their LOOKUP sheet header
const JOB_LOOKUP_KEYS = {
  alt_role_title: 'standard_name',
  legal_entity: 'legal_entity',
  business_unit: 'business_unit',
  role_type: 'role_type',
  level: 'level',
  work_environment: 'work_environment',
};

// Maps template field keys → internal row field keys. Read by key, not header
// text, since headers show org-specific labels (e.g. "Company" for legal_entity).
export const EXCEL_COLUMN_MAP = {
  job_code: 'jobCode',
  alt_role_title: 'standardIndustryName',
  role: 'jobTitle',
  legal_entity: 'legalEntity',
  business_unit: 'businessUnit',
  function: 'functionDept',
  sub_function: 'subFunction',
  specialty: 'specialty',
  role_type: 'roleType',
  level: 'jobLevel',
  work_environment: 'workEnvironment',
  location: 'location',
  role_context: 'roleContext',
  // role_reporting_to (display text) is human-readable only; the manager JD's
  // sb_unique_id comes from its hidden lookup column
  is_apex_role: 'isApexRole',
  role_reporting_to_sb_unique_id: 'roleReportingToSbUniqueId',
};

export const EMPTY_ROW = {
  source: 'manual',
  jobCode: null, standardIndustryName: '',
  jobTitle: '', legalEntity: null, businessUnit: null,
  functionDept: null, subFunction: null, specialty: [], roleType: null,
  jobLevel: null, workEnvironment: null, location: null,
  file: null, fileError: null, roleContext: null,
  roleReportingToSbUniqueId: null, isApexRole: false,
  // Field → value from Excel that failed validation, shown on the row until the field is re-picked
  invalidFields: {},
};

// Fields cleared when a parent org-structure field changes
export const CASCADE_RESETS = {
  legalEntity: { businessUnit: null, functionDept: null, subFunction: null, specialty: [], location: null },
  businessUnit: { functionDept: null, subFunction: null, specialty: [] },
  functionDept: { subFunction: null, specialty: [] },
  subFunction: { specialty: [] },
};

// Fields filled from a Job Code, cleared with it
const JOB_CODE_RESET = {
  standardIndustryName: '', legalEntity: null, roleType: null, jobLevel: null, ...CASCADE_RESETS.legalEntity,
};

const isEmptyValue = (v) => v === null || v === '' || (Array.isArray(v) && !v.length);

// Invalid fields the user has not re-picked yet
export const getUnresolvedInvalidFields = (row) =>
  Object.keys(row.invalidFields || {}).filter(field => isEmptyValue(row[field]));

export const getCascade = (orgStructure, legalEntity, businessUnit, functionDept, subFunction) => {
  const entity = orgStructure?.find(e => e.legal_entity === legalEntity);
  const allBUs = entity?.business_units || [];
  const uniqueBUs = [...new Map(allBUs.map(b => [b.business_unit, b])).values()];
  // A BU or Function listed more than once is merged, same as the backend
  const buFunctions = allBUs.filter(b => b.business_unit === businessUnit).flatMap(b => b.functions || []);
  const funcSubFunctions = buFunctions.filter(f => f.function === functionDept).flatMap(f => f.sub_functions || []);
  const selectedSF = funcSubFunctions.find(sf => sf.sub_function === subFunction);
  const uniqueOptions = (items, key) => [...new Set(items.map(i => i[key]))].map(v => ({ value: v, label: v }));
  return {
    businessUnits: uniqueBUs.map(b => ({ value: b.business_unit, label: b.business_unit })),
    functions: uniqueOptions(buFunctions, 'function'),
    subFunctions: uniqueOptions(funcSubFunctions, 'sub_function'),
    specialties: (selectedSF?.specialties || []).map(s => ({ value: s.specialty, label: s.specialty })),
    locations: (entity?.locations || []).map(l => ({ value: l.name, label: l.name })),
  };
};

// True when the options hold at least one real specialty (not only "ALL - All")
export const hasRealSpecialties = (specialties) => specialties.some(o => !isSelectAll(o.value));

// Specialty is required only when the row's sub-function has real specialties (same as backend).
// Empty arrays (multi-select fields like specialty) count as missing.
export const getMissingFields = (row, requiredFields, orgStructure) =>
  requiredFields
    .filter((f) => f.key !== 'specialty' || hasRealSpecialties(
      getCascade(orgStructure, row.legalEntity, row.businessUnit, row.functionDept, row.subFunction).specialties))
    .filter((f) => !row[f.key] || (Array.isArray(row[f.key]) && !row[f.key].length))
    .map((f) => f.label);

// Comma-joined specialty text → valid options. Any unknown value returns [] and the
// caller flags the row so the user re-picks.
export const resolveSpecialty = (text, options) => {
  const normalize = (s) => String(s || '').split(',').map(p => p.trim()).filter(Boolean).join(',');
  const resolved = resolveMulti(String(text || '').trim(), options.map(o => o.value));
  const valid = filterSelected(resolved, options);
  if (valid.length !== resolved.length || normalize(resolved.join(',')) !== normalize(text)) return [];
  return expandSentinels(valid, options);
};

// Case-insensitive key, same as Excel's VLOOKUP
const lookupKey = (value) => String(value ?? '').trim().toLowerCase();

// Reads a lookup sheet into key → { header: value } using its row 1 headers.
// Keeps the first row for a repeated key, same as VLOOKUP.
const readLookup = (sheet, keyHeader) => {
  const lookup = new Map();
  if (!sheet) return lookup;
  const [headers = [], ...rows] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  const keyIdx = headers.indexOf(keyHeader);
  if (keyIdx < 0) return lookup;
  rows.forEach(r => {
    const key = lookupKey(r[keyIdx]);
    if (!lookup.has(key)) lookup.set(key, Object.fromEntries(headers.map((h, i) => [h, r[i]])));
  });
  return lookup;
};

// Field key for each template column. FIELD_KEYS row 2 holds each key's header label, so columns are
// found by header even after a tool like Google Sheets drops or moves one. Every header must be in the
// file once; Specialty follows the org's metadata. Returns the column keys, null for an older template
// without row 2, or an error result.
const resolveColumnKeys = (headers, rows, keysSheet, specialty) => {
  const [fieldKeys = [], fieldLabels = []] = XLSX.utils.sheet_to_json(keysSheet, { header: 1, defval: '' });
  if (!fieldLabels.some(Boolean)) return null;
  const labelByKey = new Map(fieldKeys.map((key, i) => [key, fieldLabels[i]]).filter(([key, label]) => key && label));
  // FIELD_KEYS has Specialty only while the org uses it; otherwise its hidden column has the metadata label
  if (!labelByKey.has('specialty')) labelByKey.set('specialty', specialty.label || 'Specialty');
  // Keys per header in template order, so fields an org labelled alike are read left to right
  const keysByHeader = new Map();
  labelByKey.forEach((label, key) => keysByHeader.set(lookupKey(label), [...(keysByHeader.get(lookupKey(label)) || []), key]));
  const seen = new Map();
  const duplicated = new Set();
  const columnKeys = headers.map(h => {
    const keys = keysByHeader.get(lookupKey(h));
    if (!keys) return '';
    const n = seen.get(keys) || 0;
    seen.set(keys, n + 1);
    if (n < keys.length) return keys[n];
    keys.forEach(k => duplicated.add(k));
    return '';
  });

  if (!specialty.enabled) {
    // The template keeps a hidden Specialty column for these orgs, so only values in it are an error
    const specialtyIdx = columnKeys.indexOf('specialty');
    if (specialtyIdx >= 0 && rows.some(r => String(r[specialtyIdx] ?? '').trim())) return { error: SPECIALTY_NOT_USED };
    if (specialtyIdx >= 0) columnKeys[specialtyIdx] = '';
    labelByKey.delete('specialty');
  }
  // row_index is not read, so losing it (e.g. a hidden column dropped on conversion) is fine
  const readKeys = [...labelByKey].filter(([key]) => key !== 'row_index');
  const labelsOf = (entries) => [...new Set(entries.map(([, label]) => label))];
  const missing = readKeys.filter(([key]) => !columnKeys.includes(key));
  if (missing.length) return { error: MISSING_COLUMNS, columns: labelsOf(missing) };
  const repeated = readKeys.filter(([key]) => duplicated.has(key));
  if (repeated.length) return { error: DUPLICATE_COLUMNS, columns: labelsOf(repeated) };
  return columnKeys;
};

const matchOption = (value, options) => options.find(o => lookupKey(o.value) === lookupKey(value))?.value;

// Checks a row top-down: Job Code > Legal Entity > Business Unit > Function > Sub-function > Specialty.
// The first invalid level and every field below it are cleared, and its value is kept in invalidFields.
// A list with no options, or a field the org hides (the user can't re-pick it), is skipped.
const validateRow = (row, specialtyText, { orgStructure, resolveJobLevel, options, hiddenFields }) => {
  const invalidFields = {};
  const fail = (field, value) => {
    invalidFields[field] = String(value);
    Object.assign(row, { [field]: EMPTY_ROW[field] }, field === 'jobCode' ? JOB_CODE_RESET : CASCADE_RESETS[field]);
  };
  const check = (field, fieldOptions) => {
    if (isEmptyValue(row[field]) || !fieldOptions.length || hiddenFields.has(field)) return;
    const match = matchOption(row[field], fieldOptions);
    if (match === undefined) fail(field, row[field]);
    else row[field] = match;
  };
  const cascade = () => getCascade(orgStructure, row.legalEntity, row.businessUnit, row.functionDept, row.subFunction);

  if (row.jobCode && options.jobs.length && !hiddenFields.has('jobCode')) {
    const job = options.jobs.find(o => lookupKey(o.value) === lookupKey(row.jobCode))?.allFields;
    if (!job) {
      fail('jobCode', row.jobCode);
    } else {
      // The Job Code's values win over the file for the fields it fills
      Object.assign(row, {
        jobCode: job.job_code,
        standardIndustryName: job.job_title || row.standardIndustryName,
        legalEntity: job.legal_entity || row.legalEntity,
        businessUnit: job.business_unit || row.businessUnit,
        roleType: job.role_type || row.roleType,
        jobLevel: (job.level && resolveJobLevel(job.level)) || row.jobLevel,
      });
    }
  }
  check('legalEntity', orgStructure.map(e => ({ value: e.legal_entity })));
  check('businessUnit', cascade().businessUnits);
  check('functionDept', cascade().functions);
  check('subFunction', cascade().subFunctions);
  // Specialty is comma-joined in Excel; checked only once its sub-function is valid
  if (row.subFunction) {
    const { specialties } = cascade();
    row.specialty = resolveSpecialty(specialtyText, specialties);
    // A sub-function with no specialties has nothing to pick, so the text is dropped without a warning
    if (specialtyText && !row.specialty.length && specialties.length) invalidFields.specialty = specialtyText;
  } else {
    row.specialty = [];
  }
  check('location', cascade().locations);
  check('roleType', options.roleTypes);
  check('jobLevel', options.jobLevels);
  check('workEnvironment', options.workEnvironments);
  row.invalidFields = invalidFields;
};

// Reads template rows by the FIELD_KEYS sheet. Returns null for an outdated template (no FIELD_KEYS,
// or no header labels in it) and { error } for a header problem.
// Formula cells read blank if the file was never saved in Excel, so they fall back to the file's lookup sheets.
export const parseTemplateRows = (workbook, { orgStructure, resolveJobLevel, specialty, options, hiddenFields = new Set() }) => {
  const sheetName = workbook.SheetNames.includes(TEMPLATE_SHEET) ? TEMPLATE_SHEET : workbook.SheetNames[0];
  if (!workbook.SheetNames.includes(TEMPLATE_SHEET)) {
    console.warn(`[BulkJdUpload] Sheet "${TEMPLATE_SHEET}" not found, falling back to "${sheetName}"`);
  }
  const keysSheet = workbook.Sheets[FIELD_KEYS_SHEET];
  if (!keysSheet) return null;

  const [headers = [], ...aoaRows] = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: '' });
  const columnKeys = resolveColumnKeys(headers, aoaRows, keysSheet, specialty);
  if (!Array.isArray(columnKeys)) return columnKeys ?? null;

  const mappedFieldKeys = Object.values(EXCEL_COLUMN_MAP);
  const jobLookup = readLookup(workbook.Sheets[JOB_LOOKUP_SHEET], 'job_code');
  const reportingToLookup = readLookup(workbook.Sheets[REPORTING_TO_LOOKUP_SHEET], 'display');
  const cellText = (cells, key) => String(cells[columnKeys.indexOf(key)] ?? '').trim();

  return aoaRows
    .map((cells, idx) => {
      const rowData = { ...EMPTY_ROW, source: 'excel', key: `excel-${idx}-${Date.now()}` };
      const jobCode = cellText(cells, 'job_code');
      const reportingTo = cellText(cells, 'role_reporting_to');
      const jobRow = jobCode ? jobLookup.get(lookupKey(jobCode)) : null;
      const managerRow = reportingTo ? reportingToLookup.get(lookupKey(reportingTo)) : null;
      const lookupValue = (columnKey) => (columnKey === 'role_reporting_to_sb_unique_id'
        ? managerRow?.sb_unique_id
        : jobRow?.[JOB_LOOKUP_KEYS[columnKey]]);
      columnKeys.forEach((columnKey, colIdx) => {
        const field = EXCEL_COLUMN_MAP[columnKey];
        if (!field) return;
        const cellVal = cells[colIdx] ?? '';
        const val = cellVal !== '' ? cellVal : (lookupValue(columnKey) ?? '');
        if (field === 'jobLevel') {
          rowData.jobLevel = resolveJobLevel(val);
        } else if (field === 'isApexRole') {
          rowData.isApexRole = String(val).trim().toLowerCase() === 'true';
        } else {
          rowData[field] = val !== '' ? String(val).trim() : null;
        }
      });
      // Hidden column W has no header, so header-read templates take the manager from the lookup sheet
      rowData.roleReportingToSbUniqueId ??= managerRow?.sb_unique_id || null;
      // Orgs without Specialty have no column, so the row keeps the empty [] default (not text)
      const specialtyText = typeof rowData.specialty === 'string' ? rowData.specialty : '';
      validateRow(rowData, specialtyText, { orgStructure, resolveJobLevel, options, hiddenFields });
      // An apex role has no manager
      if (rowData.isApexRole) rowData.roleReportingToSbUniqueId = null;
      return rowData;
    })
    .filter(row =>
      mappedFieldKeys.some(k => k !== 'isApexRole' && row[k] !== null && row[k] !== ''
        && !(Array.isArray(row[k]) && !row[k].length))
      || row.isApexRole
      || row.roleReportingToSbUniqueId
      || Object.keys(row.invalidFields).length
    );
};
